/**
 * DemandLayerEditModal - 三层需求编辑模态框
 * 
 * 支持编辑三层需求的核心字段：
 * - statement: 当前表述（客户口径优先）
 * - gapDescription: 缺口描述
 * - nextQuestion: 下一步验证问题
 */
import { useState, useEffect } from 'react'
import { X, Building2, User, FileCode, Save, Loader2 } from 'lucide-react'
import { clsx } from 'clsx'
import type { DemandLayerItem, DemandLayerType, DemandLayerUpdate } from '@/types'

interface DemandLayerEditModalProps {
  isOpen: boolean
  onClose: () => void
  layer: DemandLayerItem | null
  onSave: (layerType: DemandLayerType, data: DemandLayerUpdate) => Promise<void>
  isSaving?: boolean
}

// 层级配置
const LAYER_CONFIG: Record<DemandLayerType, {
  label: string
  description: string
  color: string
  bgColor: string
  Icon: React.ElementType
  placeholder: string
}> = {
  organization: {
    label: '组织层（业务闭环）',
    description: '主要矛盾、成功口径、业务目标',
    color: 'text-purple-700',
    bgColor: 'bg-purple-100',
    Icon: Building2,
    placeholder: '例如：客户需要在Q2前完成合规改造，避免监管处罚，同时降低运营成本20%',
  },
  personal: {
    label: '个人层（收益/责任/风险）',
    description: '个人收益、责任承担、风险规避',
    color: 'text-pink-700',
    bgColor: 'bg-pink-100',
    Icon: User,
    placeholder: '例如：信息科技部总监需要在本项目中证明自己的技术决策能力，项目成功可能带来晋升机会',
  },
  solution: {
    label: '方案层（手段/清单）',
    description: '功能需求、技术参数、实施清单',
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
    Icon: FileCode,
    placeholder: '例如：需要支持国密算法、单点登录、审计日志等功能，部署在私有云环境',
  },
}

export default function DemandLayerEditModal({
  isOpen,
  onClose,
  layer,
  onSave,
  isSaving = false,
}: DemandLayerEditModalProps) {
  const [statement, setStatement] = useState('')
  const [gapDescription, setGapDescription] = useState('')
  const [nextQuestion, setNextQuestion] = useState('')

  // 当layer变化时重置表单
  useEffect(() => {
    if (layer) {
      setStatement(layer.statement || '')
      setGapDescription(layer.gapDescription || '')
      setNextQuestion(layer.nextQuestion || '')
    }
  }, [layer])

  if (!isOpen || !layer) return null

  const config = LAYER_CONFIG[layer.layer as DemandLayerType]
  if (!config) return null

  const { Icon } = config

  const handleSave = async () => {
    await onSave(layer.layer as DemandLayerType, {
      statement: statement.trim() || undefined,
      gapDescription: gapDescription.trim() || undefined,
      nextQuestion: nextQuestion.trim() || undefined,
    })
    onClose()
  }

  const hasChanges = 
    statement !== (layer.statement || '') ||
    gapDescription !== (layer.gapDescription || '') ||
    nextQuestion !== (layer.nextQuestion || '')

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* 遮罩层 */}
      <div 
        className="absolute inset-0 bg-black/50" 
        onClick={onClose}
      />
      
      {/* 模态框 */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* 头部 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className={clsx(
              'w-8 h-8 rounded-lg flex items-center justify-center',
              config.bgColor
            )}>
              <Icon className={clsx('w-4 h-4', config.color)} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                编辑{config.label}
              </h2>
              <p className="text-sm text-gray-500">{config.description}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* 内容区域 */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* 当前表述 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              当前表述 <span className="text-gray-400 font-normal">（客户口径优先）</span>
            </label>
            <textarea
              value={statement}
              onChange={(e) => setStatement(e.target.value)}
              placeholder={config.placeholder}
              rows={4}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            />
          </div>

          {/* 缺口描述 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              缺口描述 <span className="text-gray-400 font-normal">（还需要验证什么）</span>
            </label>
            <textarea
              value={gapDescription}
              onChange={(e) => setGapDescription(e.target.value)}
              placeholder="例如：尚未明确预算来源和决策流程"
              rows={2}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            />
          </div>

          {/* 下一步验证问题 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              下一步验证问题 <span className="text-gray-400 font-normal">（追问建议）</span>
            </label>
            <textarea
              value={nextQuestion}
              onChange={(e) => setNextQuestion(e.target.value)}
              placeholder="例如：预算审批需要经过哪些部门？预计多久能完成？"
              rows={2}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            />
          </div>

          {/* 关联证据提示 */}
          {layer.evidenceIds && layer.evidenceIds.length > 0 && (
            <div className="text-xs text-gray-500 bg-gray-50 px-3 py-2 rounded-lg">
              已关联 {layer.evidenceIds.length} 条证据
            </div>
          )}
        </div>

        {/* 底部操作栏 */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || !hasChanges}
            className={clsx(
              'px-4 py-2 text-sm font-medium rounded-lg flex items-center gap-2 transition-colors',
              hasChanges && !isSaving
                ? 'bg-primary-600 text-white hover:bg-primary-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            )}
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                保存中...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                保存
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
